// Minimal i18n: Macedonian is the source language and the text itself is the
// key. t('Зачувај') returns the string as-is in MK and looks it up in the
// English dictionary in EN — a missing English entry falls back to Macedonian,
// so nothing ever renders blank.
//
//   t('Фактура #{number} е зачувана', { number: 12 })
//
// t() works anywhere (components, utils, alerts). On a language switch App
// re-renders (it calls useLanguage()), and with it the whole tree — without
// remounting, so open forms keep their input. Text computed at module load, or
// inside useMemo/useCallback with [] deps, will NOT update: call t() at render
// time, or add `lang` from useLanguage() to the deps.
//
// Invoice / delivery-note DOCUMENTS (A4 preview, print) intentionally stay
// Macedonian — they are issued to customers.
import { useSyncExternalStore } from 'react';
import en from './en';

const STORAGE_KEY = 'lang';
export const LANGUAGES = [
  { code: 'mk', label: 'Македонски', short: 'MK' },
  { code: 'en', label: 'English', short: 'EN' },
];

function readStored() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'mk';
  } catch {
    return 'mk';
  }
}

let current = readStored();
const listeners = new Set();

function applyHtmlLang() {
  if (typeof document !== 'undefined') document.documentElement.lang = current;
}
applyHtmlLang();

export function getLanguage() {
  return current;
}

export function setLanguage(lang) {
  const next = lang === 'en' ? 'en' : 'mk';
  if (next === current) return;
  current = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  applyHtmlLang();
  listeners.forEach((l) => l());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---- Per-user persistence -------------------------------------------------
// The language is saved on the user's account (PUT /me/language) and comes
// back with login and GET /me. localStorage stays as a cache so the first
// paint is already in the right language.
//
// GET /me runs on every page change. A response to a request sent before the
// user's last switch was SAVED may still carry the old value and must not undo
// the switch, so server values are applied only for requests that started
// after the last save finished (never while a save is in flight).
const apiUrl = process.env.REACT_APP_API_URL;
let trustServerAfter = 0; // Infinity while a save is in flight

// User picked a language in the menu: apply now, save to the account.
export function chooseLanguage(lang) {
  setLanguage(lang);
  const token = (() => { try { return localStorage.getItem('token'); } catch { return null; } })();
  if (!token || !apiUrl) return;
  trustServerAfter = Infinity;
  fetch(`${apiUrl}/me/language`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ language: current }),
  })
    .then((res) => { if (!res.ok) console.error('Failed to save language:', res.status); })
    .catch((err) => console.error('Failed to save language:', err))
    .finally(() => { trustServerAfter = Date.now(); });
}

// Language that came from the server (login response, GET /me).
// `requestedAt` = Date.now() taken just before the request was sent.
export function applyAccountLanguage(lang, requestedAt = Date.now()) {
  if (lang !== 'mk' && lang !== 'en') return;
  if (requestedAt < trustServerAfter) return;
  setLanguage(lang);
}

export function t(text, vars) {
  if (text == null) return text;
  let out = current === 'en' ? (en[text] ?? text) : text;
  if (vars) {
    out = out.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : m));
  }
  return out;
}

export function useLanguage() {
  const lang = useSyncExternalStore(subscribe, getLanguage, getLanguage);
  return { lang, setLanguage, t };
}
